import React, { useMemo } from "react";
import { STADIUM_VIEWBOX, ZONE_PATHS, pathCentroid } from "@/data/stadiumPaths";

const riskColor = (pct) => {
  if (pct >= 0.85) return "hsl(var(--destructive))";        // red
  if (pct >= 0.7)  return "hsl(var(--warning))";            // amber
  if (pct >= 0.5)  return "hsl(var(--primary))";            // cyan
  return "hsl(var(--muted-foreground))";
};

export const StadiumMap = ({ zones = [], alerts = [], onZoneClick }) => {
  const alertByZone = useMemo(() => {
    const m = {};
    for (const a of alerts) {
      if (a.status === "resolved") continue;
      const score = a.severity === "critical" ? 1 : a.severity === "high" ? 0.7 : 0.4;
      m[a.zone_id] = Math.max(m[a.zone_id] || 0, score);
    }
    return m;
  }, [alerts]);

  const zoneMap = useMemo(() => Object.fromEntries(zones.map((z) => [z.id, z])), [zones]);

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border shrink-0">
        <span className="h-widget">Stadium Risk Map</span>
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "hsl(var(--muted-foreground))" }} /> low</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "hsl(var(--primary))" }} /> med</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "hsl(var(--warning))" }} /> high</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "hsl(var(--destructive))" }} /> crit</span>
        </div>
      </div>

      <div className="flex-1 grid-bg p-3 min-h-0 overflow-hidden" data-testid="stadium-map">
        <svg viewBox={STADIUM_VIEWBOX} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
          <defs>
            <radialGradient id="pitchGlow" cx="50%" cy="50%" r="60%">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.18" />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Stadium blueprint backdrop (Wankhede Stadium) */}
          <image
            href="/stadium-backdrop.png"
            x="0" y="0" width="1000" height="720"
            preserveAspectRatio="xMidYMid meet"
            opacity="0.18"
            style={{ pointerEvents: "none" }}
          />

          {/* Pitch */}
          <ellipse cx="500" cy="360" rx="220" ry="160" fill="hsl(var(--surface-elevated))" stroke="hsl(var(--border))" strokeWidth="1.5" />
          <ellipse cx="500" cy="360" rx="220" ry="160" fill="url(#pitchGlow)" />
          <rect x="486" y="320" width="28" height="80" fill="hsl(var(--muted))" stroke="hsl(var(--border))" />
          <text x="500" y="365" textAnchor="middle" className="font-mono" fontSize="10" fill="hsl(var(--muted-foreground))" letterSpacing="3">Pitch</text>

          {/* Zones */}
          {Object.entries(ZONE_PATHS).map(([zid, d]) => {
            const z = zoneMap[zid];
            const occPct = z && z.capacity ? (z.current_occupancy || 0) / z.capacity : 0;
            const alertBoost = alertByZone[zid] || 0;
            const score = Math.min(1, occPct * 0.85 + alertBoost * 0.45);
            const fill = riskColor(score);
            return (
              <g key={zid} className="cursor-pointer" onClick={() => onZoneClick?.(z)} data-testid={`map-zone-${zid}`}>
                <path
                  d={d}
                  fill={fill}
                  fillOpacity={0.18 + score * 0.55}
                  stroke={fill}
                  strokeOpacity={0.7}
                  strokeWidth={1}
                />
                {alertBoost > 0.6 && (
                  <path d={d} fill="none" stroke="hsl(var(--destructive))" strokeWidth="1.5" className="flow-path" />
                )}
                {z && (
                  <text
                    x={pathCentroid(d).x}
                    y={pathCentroid(d).y}
                    textAnchor="middle"
                    fontSize="9"
                    fill="hsl(var(--foreground))"
                    fontFamily="IBM Plex Mono, monospace"
                    letterSpacing="1"
                  >
                    {z.name}
                  </text>
                )}
              </g>
            );
          })}

          {/* Flow arrows (decorative) */}
          <path d="M100,360 Q300,200 500,180" fill="none" stroke="hsl(var(--primary))" strokeWidth="1" className="flow-path" opacity="0.6" />
          <path d="M900,360 Q700,540 500,560" fill="none" stroke="hsl(var(--primary))" strokeWidth="1" className="flow-path" opacity="0.6" />
        </svg>
      </div>
    </div>
  );
};

export default StadiumMap;
